/**
 * BudgetHealthBadge Component
 * Renders a budget health status straight from the API-provided name +
 * color fields (BudgetHealthStatus lookup drives the palette, NOT the UI).
 *
 * Fallback map (only used when the API fields are missing):
 *  1 = OK (green), 2 = Warning (yellow), 3 = Critical (orange),
 *  4 = Exceeded (red), 5 = No Budget (gray)
 */

import { Badge, type ThemedBadgeProps } from '@/components/ui';

interface BudgetHealthBadgeProps extends Omit<ThemedBadgeProps, 'children' | 'color'> {
  statusID?: number | null;
  /** healthStatusName from the API row — always preferred. */
  name?: string | null;
  /** healthStatusColor from the API row — always preferred. */
  color?: string | null;
}

const FALLBACK: Record<number, { label: string; color: string }> = {
  1: { label: 'OK', color: 'green' },
  2: { label: 'Warning', color: 'yellow' },
  3: { label: 'Critical', color: 'orange' },
  4: { label: 'Exceeded', color: 'red' },
  5: { label: 'No Budget', color: 'gray' },
};

export function BudgetHealthBadge({
  statusID,
  name,
  color,
  variant = 'light',
  size = 'sm',
  ...props
}: BudgetHealthBadgeProps) {
  const fallback = statusID != null ? FALLBACK[statusID] : undefined;
  const label = name || fallback?.label;
  const badgeColor = color || fallback?.color || 'gray';

  if (!label) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        Unknown
      </Badge>
    );
  }

  return (
    <Badge variant={variant} size={size} color={badgeColor} {...props}>
      {label}
    </Badge>
  );
}
